import { useState, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, FolderKanban, Download, X, MapPin,
  ThumbsUp, AlertTriangle, Loader2, Send, ClipboardList, Wrench,
  Camera, Paperclip, FileText, Tag, Trash2, ChevronLeft, ChevronRight,
} from "lucide-react";
import JSZip from "jszip";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, Textarea } from "@/components/ui";
import { getAssetSignedUrl, getAssetSignedUrls } from "./hopUpload";
import PackAcceptanceDocsSection from "./PackAcceptanceDocsSection";
import RaiseDefectModal from "@/features/defects/RaiseDefectModal";
import { format, parseISO } from "date-fns";

// =============================================================================
// HopCentrePackDetailScreen — /acceptance/:packId
// T-010 C.3c — full review surface for one HOP pack.
//
// Cross-project view: photo grid grouped by category → subcategory → instance,
// lightbox per photo, single + zip download, defects display, manager actions.
//
// Both surfaces principle: this screen lives alongside the existing
// /projects/:projectId/hop/:packId structural overview. They link to each
// other; managers triaging across projects use this one, project-context
// users use the existing one.
// =============================================================================

type PackStatus = "open" | "submitted" | "accepted" | "defects_raised";
type AssetType  = "photo" | "document";

interface PackDetail {
  pack_id:           string;
  pack_number:       string;
  title:             string | null;
  notes:             string | null;
  status:            PackStatus;
  label_prefix:      string | null;
  created_at:        string;
  submitted_at:      string | null;
  accepted_at:       string | null;
  defects_raised_at: string | null;
  defects_notes:     string | null;
  submitted_by_employee_id: string | null;
  template?:         { template_code: string; display_name: string } | null;
  project?:          { project_id: string; project_code: string; project_name: string } | null;
}

interface PackCategory {
  pack_category_id: string;
  code:             string;
  display_name:     string;
  description:      string | null;
  required_qty:     number;
  sort_order:       number;
  is_custom:        boolean;
}

interface PackSubcategory {
  pack_subcategory_id: string;
  pack_category_id:    string;
  code:                string;
  display_name:        string;
  hint:                string | null;
  sort_order:          number;
}

interface PackInstance {
  instance_id:      string;
  pack_category_id: string;
  instance_code:    string;
  instance_label:   string | null;
  sort_order:       number;
}

interface Asset {
  asset_id:                string;
  pack_category_id:        string | null;
  pack_subcategory_id:     string | null;
  instance_id:             string | null;
  asset_type:              AssetType;
  storage_path:            string;
  mime_type:               string | null;
  file_size_bytes:         number | null;
  file_name:               string | null;
  description:             string | null;
  gps_lat:                 number | null;
  gps_lng:                 number | null;
  gps_source:              "exif" | "device_current" | null;   // T-199
  taken_at:                string | null;
  captured_at:             string;
  watermarked:             boolean;
  captured_by_employee_id: string | null;
}

const PACK_TONE: Record<PackStatus, "open" | "pending" | "approved" | "rejected"> = {
  open:           "open",
  submitted:      "pending",
  accepted:       "approved",
  defects_raised: "rejected",
};

const PACK_LABEL: Record<PackStatus, string> = {
  open:           "in progress",
  submitted:      "awaiting review",
  accepted:       "accepted",
  defects_raised: "defects raised",
};

// Per-instance category codes (mirrors HopNewPackScreen.PER_INSTANCE_CODES).
// Future: drive from a hop_photo_template_categories.expands_per enum.
// T-413 review fix — cabinet family added; was missing here (see HopCategoryScreen).
const PER_INSTANCE_CODES = new Set([
  "sector", "active_antenna", "microwave",
  "cabinet", "rbs_cabinet", "shelter", "bbs_cabinet",
]);

// -----------------------------------------------------------------------------
// Filename helpers — used by downloadAllAsZip to build clean export filenames.
// -----------------------------------------------------------------------------
function safeNamePart(s: string | null | undefined): string {
  return (s ?? "")
    .replace(/[^A-Za-z0-9-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// -----------------------------------------------------------------------------

export default function HopCentrePackDetailScreen() {
  const { packId } = useParams<{ packId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = hasRole(user, "manager");

  // T-215 — siblings + index let lightbox prev/next nav through subcategory.
  const [lightbox, setLightbox] = useState<{ siblings: Asset[]; index: number } | null>(null);
  const [showDefectsModal, setShowDefectsModal] = useState(false);
  const [showRaiseDefect, setShowRaiseDefect] = useState(false);
  const [defectsText, setDefectsText] = useState("");
  const [defectReason, setDefectReason] = useState("");
  const [defectPriority, setDefectPriority] = useState("");
  const [zipBuilding, setZipBuilding] = useState<{ done: number; total: number } | null>(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"photos" | "documents">(searchParams.get("tab") === "documents" ? "documents" : "photos");

  // -- Load pack -----------------------------------------------------------
  const { data: pack, isLoading: loadingPack } = useQuery({
    queryKey: ["hop_centre_pack", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`pack_id, pack_number, title, notes, status, label_prefix,
                 created_at, submitted_at, accepted_at, defects_raised_at,
                 defects_notes, submitted_by_employee_id,
                 template:template_id(template_code, display_name),
                 project:project_id(project_id, project_code, project_name)`)
        .eq("pack_id", packId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as PackDetail | null;
    },
  });

  // -- Load categories / subcategories / instances / assets in parallel ----
  const { data: categories = [] } = useQuery({
    queryKey: ["hop_centre_pack_cats", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_categories")
        .select("pack_category_id, code, display_name, description, required_qty, sort_order, is_custom")
        .eq("pack_id", packId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackCategory[];
    },
  });

  const categoryIds = useMemo(() => categories.map(c => c.pack_category_id), [categories]);

  const { data: subcategories = [] } = useQuery({
    queryKey: ["hop_centre_pack_subcats", packId, categoryIds.join(",")],
    enabled: !!packId && categoryIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_subcategories")
        .select("pack_subcategory_id, pack_category_id, code, display_name, hint, sort_order")
        .in("pack_category_id", categoryIds)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackSubcategory[];
    },
  });

  const { data: instances = [] } = useQuery({
    queryKey: ["hop_centre_pack_insts", packId, categoryIds.join(",")],
    enabled: !!packId && categoryIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_category_instances")
        .select("instance_id, pack_category_id, instance_code, instance_label, sort_order")
        .in("pack_category_id", categoryIds)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackInstance[];
    },
  });

  const { data: assets = [], refetch: refetchAssets } = useQuery({
    queryKey: ["hop_centre_pack_assets", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_assets")
        .select(`asset_id, pack_category_id, pack_subcategory_id, instance_id,
                 asset_type, storage_path, mime_type, file_size_bytes, file_name,
                 description, gps_lat, gps_lng, gps_source, taken_at, captured_at,
                 watermarked, captured_by_employee_id`)
        .eq("pack_id", packId!)
        .order("captured_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Asset[];
    },
  });

  // -- Resolve signed URLs in batch (photos only) --------------------------
  const photoPaths = useMemo(
    () => assets.filter(a => a.asset_type === "photo").map(a => a.storage_path),
    [assets],
  );
  const { data: signedUrls = new Map<string, string>() } = useQuery({
    queryKey: ["hop_centre_signed_urls", photoPaths.join("|")],
    enabled: photoPaths.length > 0,
    queryFn: () => getAssetSignedUrls(photoPaths, 60 * 30),
    staleTime: 25 * 60 * 1000,
  });

  // -- Manager actions ------------------------------------------------------
  const acceptMut = useMutation({
    mutationFn: async () => {
      if (!packId) throw new Error("Missing pack id");
      const { error } = await supabase.schema("app").rpc("accept_hop_pack", { p_pack_id: packId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hop_centre_pack", packId] });
      qc.invalidateQueries({ queryKey: ["hop_centre_kpis"] });
      qc.invalidateQueries({ queryKey: ["hop_centre_awaiting"] });
    },
  });

  const raiseMut = useMutation({
    mutationFn: async (notes: string) => {
      if (!packId) throw new Error("Missing pack id");
      const { error } = await supabase.schema("app").rpc("raise_hop_defects", {
        p_pack_id: packId,
        p_notes:   notes,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hop_centre_pack", packId] });
      qc.invalidateQueries({ queryKey: ["hop_centre_kpis"] });
      qc.invalidateQueries({ queryKey: ["hop_centre_awaiting"] });
      setShowDefectsModal(false);
      setDefectsText("");
    },
  });

  // Delete-empty-pack — manager+ only, server-side guard rejects if any
  // assets exist (migration 045). Used to clean up accidentally-created or
  // test packs.
  const deleteMut = useMutation({
    mutationFn: async () => {
      if (!packId) throw new Error("Missing pack id");
      const { error } = await supabase.schema("app").rpc("delete_hop_pack", { p_pack_id: packId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hop_centre_kpis"] });
      qc.invalidateQueries({ queryKey: ["hop_centre_my"] });
      qc.invalidateQueries({ queryKey: ["hop_centre_awaiting"] });
      qc.invalidateQueries({ queryKey: ["hop_centre_recent"] });
      navigate("/acceptance");
    },
  });

  function confirmDelete() {
    if (!pack) return;
    if (!window.confirm(
      `Delete pack ${pack.pack_number}?\n\nThis pack has no photos or documents. ` +
      `It'll be removed permanently along with its category scope.`
    )) return;
    deleteMut.mutate();
  }

  // -- Group assets by category → instance? → subcategory ------------------
  type Grouped = Map<string /* category_id */, {
    category: PackCategory;
    instances: PackInstance[];   // empty for non-per-instance categories
    bySubcat: Map<string /* subcat_id or "_none" */, Asset[]>;
  }>;

  const grouped: Grouped = useMemo(() => {
    const m: Grouped = new Map();
    for (const cat of categories) {
      m.set(cat.pack_category_id, {
        category: cat,
        instances: instances.filter(i => i.pack_category_id === cat.pack_category_id),
        bySubcat: new Map(),
      });
    }
    for (const a of assets) {
      if (!a.pack_category_id) continue;
      const g = m.get(a.pack_category_id);
      if (!g) continue;
      const key = a.pack_subcategory_id ?? "_none";
      const arr = g.bySubcat.get(key) ?? [];
      arr.push(a);
      g.bySubcat.set(key, arr);
    }
    return m;
  }, [categories, instances, assets]);

  // -- Download all photos as a zip ----------------------------------------
  async function downloadAllAsZip() {
    if (assets.length === 0) return;
    const downloadable = assets.filter(a => true); // include docs too
    setZipBuilding({ done: 0, total: downloadable.length });

    try {
      const zip = new JSZip();
      const subcatLookup = new Map(subcategories.map(s => [s.pack_subcategory_id, s]));
      const instLookup   = new Map(instances.map(i => [i.instance_id, i]));
      const catLookup    = new Map(categories.map(c => [c.pack_category_id, c]));

      // Per-subcategory sequence counter for clean filenames
      const seqByGroup = new Map<string, number>();

      // Fetch in chunks of 5 to avoid hammering the Worker
      const CHUNK = 5;
      let done = 0;
      for (let i = 0; i < downloadable.length; i += CHUNK) {
        const chunk = downloadable.slice(i, i + CHUNK);
        await Promise.all(chunk.map(async (asset) => {
          const url = await getAssetSignedUrl(asset.storage_path, 60 * 10);
          if (!url) return;
          const res = await fetch(url);
          if (!res.ok) return;
          const blob = await res.blob();

          // Build a clean export filename.
          // Pattern: <site>_<Category>_<Instance>_<SubcatShort>_<Seq>.<ext>
          // e.g. ANDERGROVE-NORTH_Sector_S1_01_01.jpg
          //
          // Per James 2026-04-25, the format the client acceptance teams
          // expect. Keeps the storage path (timestamp-collision-safe) separate
          // from the human-readable export name.
          const cat    = asset.pack_category_id    ? catLookup.get(asset.pack_category_id)    : null;
          const sub    = asset.pack_subcategory_id ? subcatLookup.get(asset.pack_subcategory_id) : null;
          const inst   = asset.instance_id         ? instLookup.get(asset.instance_id)         : null;

          const groupKey = `${cat?.code ?? "misc"}_${inst?.instance_code ?? ""}_${sub?.code ?? ""}`;
          const seq = (seqByGroup.get(groupKey) ?? 0) + 1;
          seqByGroup.set(groupKey, seq);

          // Strip category prefix from subcategory code if present.
          // NBN seed has subcategory codes like "Sector_01", "Construction_05" —
          // we want the short suffix "01" / "05" since the category is named
          // separately in the filename.
          const subFullCode = sub?.code ?? "";
          const subShort = (cat?.code && subFullCode)
            ? subFullCode.replace(new RegExp(`^${escapeRegex(cat.code)}_?`, "i"), "")
            : subFullCode;

          const catName = safeNamePart(cat?.display_name ?? cat?.code ?? "Misc");

          const parts = [
            pack?.label_prefix ?? pack?.pack_number ?? "pack",
            catName,
            inst?.instance_code ?? "",
            safeNamePart(subShort),
            String(seq).padStart(2, "0"),
          ].filter(Boolean);
          const ext = (asset.file_name?.split(".").pop()
                      || asset.storage_path.split(".").pop()
                      || "bin").toLowerCase();
          const filename = `${parts.join("_")}.${ext}`;

          // Folder per category for tidiness within the zip
          const folder = safeNamePart(cat?.display_name ?? "Other");
          zip.file(`${folder}/${filename}`, blob);
        }));
        done += chunk.length;
        setZipBuilding({ done, total: downloadable.length });
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(zipBlob);
      a.download = `${pack?.pack_number ?? "hop-pack"}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      window.alert(`Zip download failed: ${e?.message ?? "unknown"}`);
    } finally {
      setZipBuilding(null);
    }
  }

  // -- Render --------------------------------------------------------------
  if (!user) return null;
  if (loadingPack) return <div className="p-6 flex justify-center"><Spinner size={20}/></div>;
  if (!pack) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Card>
          <EmptyState
            title="Pack not found"
            description="This HOP pack doesn't exist or you don't have access to it."
            icon={ClipboardList}
          />
        </Card>
      </div>
    );
  }

  const totalAssets = assets.length;
  const totalPhotos = assets.filter(a => a.asset_type === "photo").length;
  const totalDocs   = assets.filter(a => a.asset_type === "document").length;

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-20">
      {/* Back */}
      <button
        onClick={() => navigate("/acceptance")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> Back to HOP Centre
      </button>

      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] font-mono text-text-muted">
            {pack.project?.project_code ?? "—"} · {pack.project?.project_name ?? "Unknown project"}
          </div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2 mt-0.5 flex-wrap">
            <FolderKanban size={20} className="text-accent" />
            {pack.pack_number}
            <Pill label={PACK_LABEL[pack.status]} tone={PACK_TONE[pack.status]} />
          </h1>
          {pack.title && (
            <p className="text-text-muted text-[13px] mt-1">{pack.title}</p>
          )}
          <div className="text-[11px] font-mono text-text-dim mt-1.5 flex items-center gap-3 flex-wrap">
            {pack.template && <span><Tag size={10} className="inline mr-0.5"/>{pack.template.template_code}</span>}
            {pack.label_prefix && <span>label prefix: {pack.label_prefix}</span>}
            <span>{totalPhotos} photo{totalPhotos === 1 ? "" : "s"}</span>
            {totalDocs > 0 && <span>{totalDocs} doc{totalDocs === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {pack.project && (
            <Link
              to={`/projects/${pack.project.project_id}/hop/${pack.pack_id}`}
              className="text-[12px] text-text-muted hover:text-accent inline-flex items-center gap-1"
            >
              <ClipboardList size={12}/> Pack overview
            </Link>
          )}
          {canManage && pack.project && (
            <Button variant="secondary" size="sm" icon={Wrench} onClick={() => setShowRaiseDefect(true)}>Log site defect</Button>
          )}
          {totalAssets > 0 && (
            <Button
              variant="secondary"
              size="sm"
              icon={Download}
              onClick={downloadAllAsZip}
              disabled={zipBuilding !== null}
            >
              {zipBuilding
                ? `Zipping ${zipBuilding.done}/${zipBuilding.total}…`
                : "Download all (.zip)"}
            </Button>
          )}
          {canManage && totalAssets === 0 && pack.status === "open" && (
            <Button
              variant="secondary"
              size="sm"
              icon={Trash2}
              onClick={confirmDelete}
              disabled={deleteMut.isPending}
              className="text-danger hover:border-danger/50"
            >
              {deleteMut.isPending ? "Deleting…" : "Delete empty pack"}
            </Button>
          )}
        </div>
      </div>

      {deleteMut.isError && (
        <Card className="mb-4 border-danger/40 bg-danger/5">
          <div className="p-3 text-[12px] text-danger">
            Delete failed: {(deleteMut.error as Error).message}
          </div>
        </Card>
      )}

      {/* Defects banner */}
      {pack.status === "defects_raised" && pack.defects_notes && (
        <Card className="mb-4 border-danger/40 bg-danger/5">
          <div className="p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-danger flex-shrink-0 mt-0.5"/>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-danger uppercase tracking-wider2">
                  Defects raised — pack needs rework
                </div>
                <p className="text-[13px] text-text mt-1 whitespace-pre-wrap">{pack.defects_notes}</p>
                {pack.defects_raised_at && (
                  <div className="text-[10px] font-mono text-text-dim mt-2">
                    {format(parseISO(pack.defects_raised_at), "dd MMM yyyy HH:mm")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Manager actions (when status=submitted, mgr+ only) */}
      {pack.status === "submitted" && canManage && (
        <Card className="mb-4 border-accent/30 bg-accent/5">
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[13px]">
              <div className="text-text font-medium">Pack ready for your review.</div>
              <div className="text-text-muted text-[12px] mt-0.5">
                Walk the photos below, then accept or raise defects.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                icon={ThumbsUp}
                onClick={() => acceptMut.mutate()}
                disabled={acceptMut.isPending}
              >
                {acceptMut.isPending ? "Accepting…" : "Accept"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={AlertTriangle}
                onClick={() => setShowDefectsModal(true)}
                disabled={raiseMut.isPending}
              >
                Raise defects
              </Button>
            </div>
          </div>
        </Card>
      )}

      {acceptMut.isError && (
        <Card className="mb-4 border-danger/40 bg-danger/5">
          <div className="p-3 text-[12px] text-danger">
            Accept failed: {(acceptMut.error as Error).message}
          </div>
        </Card>
      )}

      {/* Tabs — Photos | Documents */}
      <div className="mb-4 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit">
        <button
          type="button"
          onClick={() => setActiveTab("photos")}
          className={`px-3 py-1.5 rounded text-[12px] transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
            activeTab === "photos"
              ? "bg-accent/15 text-accent border border-accent/30"
              : "text-text-muted hover:text-text border border-transparent"
          }`}
        >
          <Camera size={12}/> Photos
          {totalPhotos > 0 && <span className="text-[10px] font-mono opacity-75">{totalPhotos}</span>}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("documents")}
          className={`px-3 py-1.5 rounded text-[12px] transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
            activeTab === "documents"
              ? "bg-accent/15 text-accent border border-accent/30"
              : "text-text-muted hover:text-text border border-transparent"
          }`}
        >
          <FileText size={12}/> Documents
        </button>
      </div>

      {/* Documents tab — acceptance category deliverables for this project */}
      {activeTab === "documents" && pack.project && (
        <PackAcceptanceDocsSection
          projectId={pack.project.project_id}
          canManage={canManage}
        />
      )}

      {/* Log a SITE defect (physical issue to go back for) — separate from the
          pack-QA "Raise defects" rework loop above. Goes to the Defect Register. */}
      {showRaiseDefect && pack.project && (
        <RaiseDefectModal
          projectId={pack.project.project_id}
          packId={pack.pack_id}
          onClose={() => setShowRaiseDefect(false)}
        />
      )}

      {/* Photos tab — categories grid */}
      {activeTab === "photos" && (categories.length === 0 ? (
        <Card>
          <EmptyState
            title="No categories on this pack"
            description="The pack creation wizard didn't tick any categories. Edit the pack scope to add some."
            icon={ClipboardList}
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {categories.map(cat => {
            const g = grouped.get(cat.pack_category_id);
            if (!g) return null;
            return (
              <CategorySection
                key={cat.pack_category_id}
                category={cat}
                instances={g.instances}
                subcategories={subcategories.filter(s => s.pack_category_id === cat.pack_category_id)}
                assetsByGroup={g.bySubcat}
                signedUrls={signedUrls}
                onOpenPhoto={(asset, siblings) => {
                  const idx = siblings.findIndex(s => s.asset_id === asset.asset_id);
                  if (idx >= 0) setLightbox({ siblings, index: idx });
                }}
              />
            );
          })}
        </div>
      ))}

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          siblings={lightbox.siblings}
          initialIndex={lightbox.index}
          getUrl={(a) => signedUrls.get(a.storage_path) ?? ""}
          packLabelPrefix={pack?.label_prefix}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Raise-defects modal */}
      {showDefectsModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !raiseMut.isPending && setShowDefectsModal(false)}
        >
          <div
            className="bg-ink-raised border border-line rounded-lg p-5 max-w-lg w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-semibold flex items-center gap-2">
                <AlertTriangle size={16} className="text-warn"/>
                Raise defects
              </h3>
              <button
                onClick={() => !raiseMut.isPending && setShowDefectsModal(false)}
                className="text-text-muted hover:text-text"
                type="button"
              >
                <X size={16}/>
              </button>
            </div>
            <p className="text-[12px] text-text-muted mb-3">
              Spell out what the crew needs to fix. The submitter gets notified
              with a snippet of these notes.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="text-[11px] text-text-muted">Reason
                <select value={defectReason} onChange={(e) => setDefectReason(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                  <option value="">— select —</option>
                  <option>Missing photos</option>
                  <option>Wrong / unclear angle</option>
                  <option>Illegible label / serial</option>
                  <option>Incomplete category</option>
                  <option>Quality / lighting</option>
                  <option>Other</option>
                </select>
              </label>
              <label className="text-[11px] text-text-muted">Priority
                <select value={defectPriority} onChange={(e) => setDefectPriority(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                  <option value="">— select —</option>
                  <option>Standard</option>
                  <option>Urgent</option>
                </select>
              </label>
            </div>
            <Textarea
              value={defectsText}
              onChange={(e) => setDefectsText(e.target.value)}
              rows={5}
              placeholder="e.g. Sector S2 photos missing the top mount detail; serial plates illegible on sweep test #3; need re-shoot of cable entry from north face."
              autoFocus
            />
            {raiseMut.isError && (
              <div className="text-[12px] text-danger mt-2">
                {(raiseMut.error as Error).message}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDefectsModal(false)}
                disabled={raiseMut.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={Send}
                onClick={() => raiseMut.mutate([defectReason && `Reason: ${defectReason}`, defectPriority && `Priority: ${defectPriority}`, defectsText.trim()].filter(Boolean).join("\n"))}
                disabled={raiseMut.isPending || defectsText.trim().length === 0}
              >
                {raiseMut.isPending ? "Sending…" : "Send to crew"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CategorySection — one collapsible section per category, with optional
// instance tabs (Sector S1 / S2 / S3) and a subcategory-grouped photo grid.
// -----------------------------------------------------------------------------
function CategorySection({
  category, instances, subcategories, assetsByGroup, signedUrls, onOpenPhoto,
}: {
  category:        PackCategory;
  instances:       PackInstance[];
  subcategories:   PackSubcategory[];
  assetsByGroup:   Map<string, Asset[]>;
  signedUrls:      Map<string, string>;
  onOpenPhoto:     (asset: Asset, siblings: Asset[]) => void;
}) {
  const isPerInstance = PER_INSTANCE_CODES.has(category.code) && instances.length > 0;
  const [activeInstanceId, setActiveInstanceId] = useState<string | null>(
    isPerInstance ? instances[0].instance_id : null,
  );

  // Filter the asset map to only those for the active instance (if applicable)
  const filteredBySubcat = useMemo(() => {
    if (!isPerInstance || !activeInstanceId) return assetsByGroup;
    const m = new Map<string, Asset[]>();
    assetsByGroup.forEach((arr, key) => {
      const filtered = arr.filter(a => a.instance_id === activeInstanceId);
      if (filtered.length > 0) m.set(key, filtered);
    });
    return m;
  }, [assetsByGroup, activeInstanceId, isPerInstance]);

  const totalInCategory = useMemo(() => {
    let n = 0;
    filteredBySubcat.forEach(arr => { n += arr.length; });
    return n;
  }, [filteredBySubcat]);

  return (
    <Card>
      <div className="p-4 md:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold flex items-center gap-2 flex-wrap">
              {category.display_name}
              {category.is_custom && (
                <span className="text-[9px] font-mono uppercase tracking-wider2 text-warn px-1.5 py-0.5 rounded bg-warn/10 border border-warn/30">custom</span>
              )}
              <span className="text-[10px] font-mono text-text-dim">{category.code}</span>
            </h2>
            {category.description && (
              <p className="text-text-muted text-[12px] mt-0.5">{category.description}</p>
            )}
          </div>
          <div className="text-[11px] font-mono text-text-muted flex-shrink-0">
            {totalInCategory} photo{totalInCategory === 1 ? "" : "s"}
          </div>
        </div>

        {/* Instance tabs */}
        {isPerInstance && (
          <div className="mb-3 flex items-center gap-1 p-1 bg-ink border border-line rounded-md w-fit overflow-x-auto">
            {instances.map(inst => {
              const active = inst.instance_id === activeInstanceId;
              return (
                <button
                  key={inst.instance_id}
                  type="button"
                  onClick={() => setActiveInstanceId(inst.instance_id)}
                  className={`px-2.5 py-1 rounded text-[11px] transition-colors whitespace-nowrap ${
                    active
                      ? "bg-accent/15 text-accent border border-accent/30"
                      : "text-text-muted hover:text-text border border-transparent"
                  }`}
                >
                  <span className="font-mono">{inst.instance_code}</span>
                  {inst.instance_label && (
                    <span className="ml-1 text-text-dim">· {inst.instance_label}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Subcategory groups */}
        {totalInCategory === 0 ? (
          <div className="text-[12px] text-text-muted italic">
            No photos uploaded {isPerInstance ? "for this instance" : "yet"}.
          </div>
        ) : (
          <div className="space-y-3">
            {subcategories.map(sub => {
              const arr = filteredBySubcat.get(sub.pack_subcategory_id) ?? [];
              if (arr.length === 0) return null;
              return (
                <SubcategoryBlock
                  key={sub.pack_subcategory_id}
                  subcategory={sub}
                  assets={arr}
                  signedUrls={signedUrls}
                  onOpenPhoto={onOpenPhoto}
                />
              );
            })}
            {/* Unsorted (no subcategory) */}
            {(filteredBySubcat.get("_none") ?? []).length > 0 && (
              <SubcategoryBlock
                subcategory={null}
                assets={filteredBySubcat.get("_none") ?? []}
                signedUrls={signedUrls}
                onOpenPhoto={onOpenPhoto}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
function SubcategoryBlock({
  subcategory, assets, signedUrls, onOpenPhoto,
}: {
  subcategory: PackSubcategory | null;
  assets:      Asset[];
  signedUrls:  Map<string, string>;
  onOpenPhoto: (asset: Asset) => void;
}) {
  const photos = assets.filter(a => a.asset_type === "photo");
  const docs   = assets.filter(a => a.asset_type === "document");

  return (
    <div className="border-t border-line pt-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-text font-medium text-[13px]">
          {subcategory?.display_name ?? "Unsorted"}
        </span>
        {subcategory && (
          <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">
            {subcategory.code}
          </span>
        )}
        <span className="text-[10px] font-mono text-text-muted ml-auto">
          {assets.length}
        </span>
      </div>
      {subcategory?.hint && (
        <p className="text-[11px] text-text-muted italic mb-2 line-clamp-2">{subcategory.hint}</p>
      )}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mb-2">
          {photos.map(p => {
            const url = signedUrls.get(p.storage_path);
            return (
              <button
                key={p.asset_id}
                type="button"
                onClick={() => onOpenPhoto(p, assets)}
                className="aspect-square relative rounded overflow-hidden border border-line hover:border-accent transition-colors group bg-ink"
              >
                {url ? (
                  <img src={url} alt="" loading="lazy" className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-text-dim">
                    <Loader2 size={14} className="animate-spin"/>
                  </div>
                )}
                {p.gps_lat !== null && p.gps_lng !== null && (
                  <div
                    className={`absolute bottom-1 left-1 px-1 py-0.5 rounded text-[9px] font-mono inline-flex items-center gap-0.5 ${
                      p.gps_source === "exif"
                        ? "bg-black/60 text-white"
                        : "bg-amber-500/80 text-black"
                    }`}
                    title={
                      p.gps_source === "exif"
                        ? "GPS from photo EXIF — actual capture location"
                        : p.gps_source === "device_current"
                          ? "GPS from device at upload time — may not be the original capture location"
                          : "GPS from legacy upload (pre-T-199) — provenance unknown"
                    }
                  >
                    <MapPin size={9}/> {p.gps_source === "exif" ? "gps" : "gps?"}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Documents */}
      {docs.length > 0 && (
        <div className="space-y-1.5">
          {docs.map(d => <DocRow key={d.asset_id} asset={d}/>)}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function DocRow({ asset }: { asset: Asset }) {
  async function open() {
    const url = await getAssetSignedUrl(asset.storage_path, 60 * 10);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }
  function fmtBytes(n: number | null): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }
  return (
    <div className="flex items-center gap-3 p-2 rounded bg-ink border border-line">
      <FileText size={14} className="text-text-muted flex-shrink-0"/>
      <button
        onClick={open}
        type="button"
        className="flex-1 min-w-0 text-left text-[12px] text-text hover:text-accent truncate"
      >
        {asset.file_name || asset.storage_path.split("/").pop()}
      </button>
      <span className="text-[10px] text-text-muted font-mono flex-shrink-0">
        {fmtBytes(asset.file_size_bytes)}
      </span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Lightbox — fullscreen photo with metadata + per-photo download.
// -----------------------------------------------------------------------------
function Lightbox({ siblings, initialIndex, getUrl, packLabelPrefix, onClose }: {
  siblings: Asset[];
  initialIndex: number;
  getUrl: (a: Asset) => string;
  packLabelPrefix?: string | null;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const asset = siblings[index];
  const url   = getUrl(asset);
  const prev = () => setIndex(i => (i - 1 + siblings.length) % siblings.length);
  const next = () => setIndex(i => (i + 1) % siblings.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowLeft")  prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, siblings.length]);

  if (!asset) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <a
        href={url}
        download={asset.file_name ?? undefined}
        onClick={e => e.stopPropagation()}
        className="absolute right-16 w-11 h-11 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black z-10" style={{ top: "calc(var(--safe-top, 0px) + 1rem)" }}
        title="Download"
      >
        <Download size={18}/>
      </a>
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 w-11 h-11 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black z-10" style={{ top: "calc(var(--safe-top, 0px) + 1rem)" }}
      >
        <X size={18}/>
      </button>

      {/* T-215 — prev/next nav (only when more than one sibling) */}
      {siblings.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black z-10"
            title="Previous (←)"
          >
            <ChevronLeft size={22}/>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black z-10"
            title="Next (→)"
          >
            <ChevronRight size={22}/>
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded bg-black/60 text-white text-[11px] font-mono">
            {index + 1} / {siblings.length}
          </div>
        </>
      )}

      <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
        <img
          src={url}
          alt=""
          className="max-w-full max-h-[88vh] object-contain rounded shadow-2xl"
        />

        {/* T-217 step 3 — CSS overlay for non-burned-in photos. All-or-nothing
            rule: only render if label + date + GPS ALL present. */}
        {!asset.watermarked
          && packLabelPrefix
          && asset.taken_at
          && asset.gps_lat !== null
          && asset.gps_lng !== null && (
          <div
            className="absolute bottom-4 left-4 px-3 py-2 bg-black/55 border border-white/15 rounded font-mono leading-tight pointer-events-none"
            style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.6)" }}
          >
            <div className="text-accent text-[14px] font-medium">{packLabelPrefix}</div>
            <div className="text-white/95 text-[12px]">
              {format(parseISO(asset.taken_at), "dd/MM/yyyy HH:mm")}
            </div>
            <div className="text-white/95 text-[12px]">
              {asset.gps_lat.toFixed(6)}, {asset.gps_lng.toFixed(6)}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
